import { useStore } from 'zustand';
import { createStore } from 'zustand/vanilla';
import type { DrawTool, ShapeKind } from '../doc/schema';
import { effectiveTheme } from '../theme/theme';

/** Default colours offered everywhere a colour is picked. */
export const PALETTE = [
  '#111111',
  '#ffffff',
  '#8d8d8d',
  '#d7263d',
  '#f46036',
  '#ffd23f',
  '#3bb273',
  '#1b998b',
  '#2b5bd7',
  '#7b2cbf',
  '#ff6fb5',
  '#5b3a29',
] as const;

/** Modes of the draw tool: the pen tools, the eraser, and the lasso that selects strokes. */
export type PenMode = DrawTool | 'eraser' | 'lasso';

/** Per-device tool defaults (persisted in localStorage, not part of the board). */
export interface ToolSettings {
  shapeKind: ShapeKind;
  fill: string;
  stroke: string;
  strokeWidth: number;
  penMode: PenMode;
  penColor: string;
  penSize: number;
  highlighterColor: string;
  highlighterSize: number;
  eraserSize: number;
  /** Stylus mode: a single finger never draws or moves things; two fingers still pan/zoom. */
  penOnly: boolean;
  /** Magnetic snapping to other items' edges and centres while moving/resizing (Alt bypasses). */
  snap: boolean;
  /** Also snap to the canvas grid. */
  snapGrid: boolean;
}

const KEY = 'vds:tools';

const defaults: ToolSettings = {
  shapeKind: 'rect',
  fill: 'none',
  stroke: '#2b5bd7',
  strokeWidth: 3,
  penMode: 'pen',
  // ink that is visible on the canvas of the theme in use when the settings were first created
  penColor: effectiveTheme() === 'dark' ? '#ececec' : '#111111',
  penSize: 4,
  highlighterColor: '#ffd23f',
  highlighterSize: 24,
  eraserSize: 24,
  penOnly: false,
  snap: true,
  snapGrid: false,
};

function load(): ToolSettings {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return { ...defaults, ...(JSON.parse(raw) as Partial<ToolSettings>) };
  } catch {
    /* ignore */
  }
  return defaults;
}

export const toolSettings = createStore<ToolSettings>(() => load());

toolSettings.subscribe((s) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* ignore */
  }
});

export function setToolSettings(patch: Partial<ToolSettings>): void {
  toolSettings.setState(patch);
}

export function useToolSettings<T>(selector: (s: ToolSettings) => T): T {
  return useStore(toolSettings, selector);
}
